import { Button, Card, CardBody, Stack } from '@chakra-ui/react';
import { NotificationCategory } from '@pelicargo/types';
import { useError } from '@pelicargo/ui';
import { addressSchema, ConditionalField } from '@pelicargo/utils';
import { FreightServiceAddOn } from '@prisma/client';
import { uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { DefaultContactsCard } from '../../components/DefaultContactsCard';
import { FlightJourneyCard } from '../../components/FlightJourneyCard';
import { InlandBookingForm } from '../../components/Forms/InlandBookingForm';
import { QuoteVariantTabs } from '../../components/QuoteVariantTabs';
import { RequestDetailsCard } from '../../components/RequestDetailsCard';
import { SimpleForm } from '../../components/SimpleForm';
import { trpc, TrpcOutputs } from '../../config/trpc';
import { useConditionalResolver } from '../../hooks/useConditionalResolver';
import { useSelectedQuoteVariant } from '../../hooks/useSelectedQuoteVariant';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { TermsAndConditions } from '../TermsAndConditions';

// These fields arrive from the server as null as often as they're simply absent,
// and a bare z.string() reports that as "Expected string, received null". Fold
// both into the empty string so the user sees the real message.
const requiredText = (message: string) => z.preprocess((value) => value ?? '', z.string().trim().min(1, message));
const now = new Date();
const todayUtcMidnight = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
const excludedConditionalFields = [ConditionalField.IS_CONSOLIDATED];

const baseSchema = z.object({
  from_dock_hours: z.coerce.number(),
  to_dock_hours: z.coerce.number(),
  terms: z.boolean().refine((value) => value === true, {
    message: 'Please accept the terms and conditions',
  }),
  pickup_ready_at: z.coerce
    .date({
      required_error: 'Pickup Ready At is required',
    })
    // Coerce defaults to a unix timestamp of 0, so this properly filters it
    // Match SimpleDatePicker UTC midnight so today validates west of UTC
    .min(todayUtcMidnight, 'Ready date cannot be in the past'),
  customer_ref: z.string().optional(),
  pickup_ref: z.string().optional(),
  freight_service_add_ons: z.nativeEnum(FreightServiceAddOn).array().optional(),
  awb_instructions: z.string().nullish(),
  additional_information: z.string().nullish(),
  inland_dropoff_contact: z
    .object({
      full_name: requiredText('Full Name is required'),
      phone_number: z.string().nullish(),
      email: z.string().nullish(),
    })
    .refine((contact) => Boolean(contact.phone_number?.trim() || contact.email?.trim()), {
      message: 'Phone or email is required',
      path: ['phone_number'],
    }),
  cargo_origin: addressSchema.extend({
    recipient: requiredText('Business name or recipient is required'),
    line_one: requiredText('Street Address is required'),
    line_two: z.string().nullish(),
    city: requiredText('City is required'),
    state: requiredText('State is required'),
    zip_code: requiredText('Zip/Postal Code is required'),
    country: requiredText('Country is required'),
  }),
  default_contacts: z.array(
    z.object({
      contact_id: z.coerce.number(),
      updates_for: z.array(z.nativeEnum(NotificationCategory, { required_error: 'Notification category is required' })),
    }),
  ),
});

type Props = {
  quote: TrpcOutputs['getQuote'];
  request: TrpcOutputs['getQuote']['request'];
};

export const ExwBooking = ({ quote, request }: Props) => {
  const { handleError } = useError();
  const navigate = useNavigate();
  const { targetAccount } = useTargetAccount();
  const { selectedQuoteVariantId } = useSelectedQuoteVariant();
  const quoteVariant = useMemo(() => {
    return quote?.quote_variants.find((variant) => variant.id === selectedQuoteVariantId);
  }, [quote?.quote_variants, selectedQuoteVariantId]);
  const inlandQuote = quoteVariant?.inland_service_quote_variant?.quote;

  const { data: requestDefaultContacts } = trpc.getDefaultContactsByRequest.useQuery(
    {
      id: quote?.request_id,
      updates_for: NotificationCategory.Booking,
    },
    { enabled: !!quote?.request_id },
  );
  const { data: accountDefaultContacts } = trpc.getDefaultContactsByAccount.useQuery(
    {
      id: targetAccount?.id,
      updates_for: NotificationCategory.Booking,
    },
    { enabled: !!targetAccount?.id },
  );
  // Unique contacts by contact_id prioritizing request contacts
  const default_contacts = uniqBy([...(requestDefaultContacts || []), ...(accountDefaultContacts || [])], 'contact_id');

  const { fields, schema, resolver } = useConditionalResolver(baseSchema, quote?.id, excludedConditionalFields);

  type FormValues = z.infer<typeof schema>;

  const methods = useForm<FormValues>({
    mode: 'onBlur',
    resolver,
    defaultValues: {
      // Left undefined rather than null when the quote has no stored date - null
      // coerces straight to 01 Jan 1970 and submits without the user noticing.
      pickup_ready_at: inlandQuote?.pickup_ready_at ?? undefined,
      // Default to 480 minutes (8:00 AM) if not set
      from_dock_hours: inlandQuote?.from_dock_hours || 480,
      // Default to 1020 minutes (17:00 PM) if not set
      to_dock_hours: inlandQuote?.to_dock_hours || 1020,
      freight_service_add_ons: request?.freight_service_add_ons || [],
      customer_ref: request?.customer_ref ?? '',
      pickup_ref: inlandQuote?.pickup_ref ?? '',
      inland_dropoff_contact: inlandQuote?.inland_dropoff_contact || {},
      additional_information: quote?.additional_information ? quote?.additional_information : '',
      awb_instructions: inlandQuote?.awb_instructions,
      cargo_origin: request?.cargo_origin as FormValues['cargo_origin'],

      hs_code: request?.hs_code,
      commodity: request.commodity,
      is_military_related: request.is_military_related ?? false,
    },
  });

  useEffect(() => {
    const fromDockHours = inlandQuote?.from_dock_hours;
    const toDockHours = inlandQuote?.to_dock_hours;
    const pickupReadyAt = inlandQuote?.pickup_ready_at;
    const inlandDropoffContact = inlandQuote?.inland_dropoff_contact;
    const additionalInformation = quote?.additional_information;
    const pickupRef = inlandQuote?.pickup_ref;

    if (fromDockHours) methods.setValue('from_dock_hours', fromDockHours);
    if (toDockHours) methods.setValue('to_dock_hours', toDockHours);
    if (pickupReadyAt) methods.setValue('pickup_ready_at', pickupReadyAt, { shouldValidate: true });
    if (inlandDropoffContact) methods.setValue('inland_dropoff_contact', inlandDropoffContact);
    if (additionalInformation) methods.setValue('additional_information', additionalInformation);
    if (pickupRef) methods.setValue('pickup_ref', pickupRef);
  }, [
    methods,
    inlandQuote?.from_dock_hours,
    inlandQuote?.inland_dropoff_contact,
    inlandQuote?.pickup_ready_at,
    inlandQuote?.to_dock_hours,
    quote?.additional_information,
    inlandQuote?.pickup_ref,
  ]);

  useEffect(() => {
    if (default_contacts?.length) {
      const contacts = default_contacts.map((contact) => ({
        contact_id: contact.contact_id,
        updates_for: contact.updates_for,
      }));
      methods.setValue(
        'default_contacts',
        contacts.map((contact) => ({
          contact_id: contact.contact_id,
          updates_for: contact.updates_for,
        })),
      );
    }
  }, [default_contacts, default_contacts?.length, methods]);

  const bookQuoteVariant = trpc.bookQuoteVariant.useMutation();

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        if (!quoteVariant) {
          handleError('Please select a quote variant.');
          return;
        }

        const requestId = quote?.request?.id;
        const quoteId = quote?.id;

        await bookQuoteVariant.mutateAsync({
          id: selectedQuoteVariantId,

          customerRef: values.customer_ref,
          pickupRef: values.pickup_ref,
          additionalInformation: values.additional_information,
          inlandDropoffContactFullName: values.inland_dropoff_contact.full_name,
          inlandDropoffContactPhoneNumber: values.inland_dropoff_contact.phone_number,
          inlandDropoffContactEmail: values.inland_dropoff_contact.email,
          fromDockHours: values.from_dock_hours,
          toDockHours: values.to_dock_hours,
          awbInstructions: values.awb_instructions,
          pickupReadyAt: values.pickup_ready_at,
          freightServiceAddOns: values.freight_service_add_ons,
          defaultContacts:
            values?.default_contacts?.map((contact) => ({
              contact_id: contact.contact_id,
              updates_for: contact?.updates_for || [],
            })) || [],
          cargoOrigin: values.cargo_origin,

          commodity: values.commodity,
          hs_code: values.hs_code,
          is_military_related: values.is_military_related,
          emergency_contact: values.emergency_contact,
        });

        navigate(`/requests/${requestId}/quotes/${quoteId}/booked`, {
          state: {
            openAwbInstructions: true,
          },
        });
      } catch (error) {
        handleError(error);
      }
    },
    [bookQuoteVariant, handleError, navigate, quote?.id, quote?.request?.id, quoteVariant, selectedQuoteVariantId],
  );

  return (
    <SimpleForm onSubmit={handleSubmit} defaultValues={{ default_contacts }} {...(methods as any)}>
      <Stack direction={{ base: 'column', lg: 'row' }} w="full" spacing="6">
        <Stack h="fit-content" w="full">
          <Stack spacing="6">
            <InlandBookingForm quote={quote} conditionalFields={fields} />
            <DefaultContactsCard notificationCategory={NotificationCategory.Booking} />
          </Stack>
        </Stack>
        <Stack w="full" spacing="6">
          <Card h="auto">
            <CardBody p="0">
              <QuoteVariantTabs
                quote={quote}
                quoteVariants={quote?.quote_variants || []}
                showBookingLink={false}
                navigateOnSelection
              />
              <Stack p="4">
                <TermsAndConditions provider={quote?.provider} />
                <Button colorScheme="primary" size="lg" type="submit" isLoading={bookQuoteVariant?.isLoading}>
                  Request Booking
                </Button>
              </Stack>
            </CardBody>
          </Card>
          <FlightJourneyCard showHeader quote={quote} quoteVariant={quoteVariant} />
          <RequestDetailsCard request={request} quote={quote} />
        </Stack>
      </Stack>
    </SimpleForm>
  );
};
